/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Adding or replacing a recipe's picture, in the editor.
 *
 * ── THE THREE SOURCES, AND WHY THEY LOOK THE SAME HERE ──────────────────────
 * A recipe photo arrives one of three ways: the household took one, it came off
 * a cookbook page they scanned, or it was captured from an imported web page.
 * Only the first is a user action, and this component is where it happens. The
 * other two arrive already attached, and the only thing this field does about
 * them is SAY where the picture came from — because "replace this" is a
 * different decision when the current image is a photo of a book page than when
 * it's the one you took of your own dinner.
 *
 * ── DEFERRED WHEN THE RECIPE DOESN'T EXIST YET ──────────────────────────────
 * The image endpoint is keyed by recipe id, and a recipe being written for the
 * first time has none. So on a new recipe this holds the data URL in state and
 * hands it back to the editor to upload immediately after the create call
 * returns an id. That is why `value`/`onChange` are pending-image props rather
 * than the component owning the upload itself.
 */

import { useState } from "react";

import { downscaleImage } from "../../lib/downscaleImage.ts";
import { Icon } from "../../components/Icon.tsx";
import { RecipeImage } from "./RecipeImage.tsx";

const SOURCE_LABEL: Record<string, string> = {
  upload: "Your photo",
  scan: "From the page you scanned",
  import: "From the website you imported",
};

export function RecipePhotoField({
  recipeId, hasImage, imageSource, value, onChange, onRemoveExisting,
}: {
  /** Absent while writing a brand-new recipe. */
  recipeId?: string;
  hasImage: boolean;
  imageSource?: string | null;
  /** A pending data URL not yet uploaded. */
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  onRemoveExisting?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      // Downscaled in the browser: a phone photo is ~4 MB and the column CHECK
      // caps a stored image at 2 MB, so an un-downscaled upload is a guaranteed
      // rejection after a long wait on mobile data.
      const { dataUrl } = await downscaleImage(file);
      onChange(dataUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read that image.");
    }
    setBusy(false);
  };

  const showing = value
    ? <img src={value} alt="" className="h-full w-full object-cover" />
    : (recipeId && hasImage)
      ? <RecipeImage recipeId={recipeId} aspect="wide" rounded={false} />
      : null;

  return (
    <div>
      <div className="label-mono mb-2">Photo</div>

      {showing ? (
        <div className="space-y-2">
          <div className="aspect-[4/3] w-full max-w-sm overflow-hidden rounded-xl2 border border-rule-soft">
            {showing}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="btn-ghost cursor-pointer text-xs">
              <Icon name="camera" className="h-4 w-4" />
              Replace
              <input type="file" accept="image/*" className="hidden"
                     onChange={(e) => { void pick(e.target.files); e.target.value = ""; }} />
            </label>
            <button
              type="button"
              className="btn-ghost text-xs !text-danger"
              onClick={() => {
                onChange(null);
                if (!value && hasImage) onRemoveExisting?.();
              }}
            >
              <Icon name="x" className="h-4 w-4" />
              Remove
            </button>
            {!value && imageSource && (
              <span className="type-meta">{SOURCE_LABEL[imageSource] ?? ""}</span>
            )}
          </div>
        </div>
      ) : (
        <label className="flex max-w-sm cursor-pointer flex-col items-center gap-2 rounded-xl2 border border-dashed border-rule p-6 text-center transition hover:border-accent">
          <input type="file" accept="image/*" className="hidden"
                 onChange={(e) => { void pick(e.target.files); e.target.value = ""; }} />
          <span aria-hidden className="grid h-10 w-10 place-items-center rounded-lg bg-accent/10 text-accent">
            <Icon name="camera" className="h-5 w-5" />
          </span>
          <span className="text-sm font-medium text-ink">
            {busy ? "Reading…" : "Add a photo"}
          </span>
          <span className="type-meta max-w-[16rem]">
            Optional. A picture of the finished dish is what makes a library
            worth browsing.
          </span>
        </label>
      )}

      {error && <p className="mt-2 text-xs text-danger">{error}</p>}
    </div>
  );
}
